import React, { useState, useEffect } from 'react';
import { Plus, AlertCircle, Lock, Users, Lightbulb, FileText, Archive } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { api } from '../../api/client';
import Navbar from '../shared/Navbar';
import Modal from '../shared/Modal';
import PasswordGate from '../shared/PasswordGate';
import ArchivedWorkshopsModal from './ArchivedWorkshopsModal';

const WorkshopsView = ({ onNavigateToWorkshop, onNavigateToUserManagement }) => {
  const { currentUser } = useAppContext();
  const [workshops, setWorkshops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const [pendingWorkshop, setPendingWorkshop] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const canCreate = currentUser.role === 'admin';
  const canManageUsers = currentUser.role === 'admin' || currentUser.role === 'facilitator';
  // Facilitators bypass the workshop password gate. This is safe without an
  // extra membership check here because the workshop list itself is already
  // scoped server-side to only workshops the facilitator is a member of
  // (GET /api/workshops) -- a facilitator can never even see a workshop they
  // don't belong to, let alone click into one.
  const isAppAdmin = currentUser.role === 'admin' || currentUser.role === 'facilitator';

  const loadWorkshops = async () => {
    setIsLoading(true);
    try {
      const data = await api.getWorkshops();
      setWorkshops(data);
    } catch (error) {
      console.error('Failed to load workshops:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadWorkshops(); }, []);

  const handleLogout = () => { api.logout(); window.location.reload(); };

  const handleWorkshopClick = (workshop) => {
    if (workshop.is_password_protected && !isAppAdmin) {
      setPendingWorkshop(workshop);
    } else {
      onNavigateToWorkshop(workshop);
    }
  };

  const resetCreateForm = () => {
    setNewName('');
    setNewDescription('');
    setIsPasswordProtected(false);
    setNewPassword('');
  };

  const createWorkshop = async () => {
    if (!newName) return;
    setIsCreating(true);
    try {
      const newWorkshop = await api.createWorkshop({
        name: newName,
        description: newDescription,
        is_password_protected: isPasswordProtected,
        workshop_password: isPasswordProtected ? newPassword : null,
      });
      setWorkshops([...workshops, { ...newWorkshop, foresight_session_count: 0, writing_session_count: 0 }]);
      setShowCreateModal(false);
      resetCreateForm();
    } catch (error) {
      alert('Failed to create workshop: ' + error.message);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar title="Workshops" onLogout={handleLogout} />

      {pendingWorkshop && (
        <PasswordGate
          title={pendingWorkshop.name}
          subtitle="This workshop is password protected."
          verify={async (password) => {
            const result = await api.verifyWorkshopPassword(pendingWorkshop.id, password);
            return result.valid;
          }}
          onUnlocked={() => { const w = pendingWorkshop; setPendingWorkshop(null); onNavigateToWorkshop(w); }}
          onCancel={() => setPendingWorkshop(null)}
        />
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Workshops</h2>
          <div className="flex space-x-3">
            {currentUser.role === 'admin' && (
              <button
                onClick={() => setShowArchiveModal(true)}
                className="flex items-center space-x-2 text-gray-600 hover:text-[#01006A] border border-gray-300 hover:border-[#01006A] px-4 py-2 rounded-lg"
              >
                <Archive className="w-5 h-5" />
                <span>Archive</span>
              </button>
            )}
            {canManageUsers && (
              <button
                onClick={onNavigateToUserManagement}
                className="flex items-center space-x-2 bg-[#01006A] text-white px-4 py-2 rounded-lg hover:bg-[#010059]"
              >
                <Users className="w-5 h-5" />
                <span>Manage Users</span>
              </button>
            )}
            {canCreate && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 bg-[#01006A] text-white px-4 py-2 rounded-lg hover:bg-[#010059]"
              >
                <Plus className="w-5 h-5" />
                <span>New Workshop</span>
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-gray-400 text-sm">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workshops.map(workshop => (
              <div
                key={workshop.id}
                onClick={() => handleWorkshopClick(workshop)}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{workshop.name}</h3>
                  {workshop.is_password_protected && (
                    <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full flex-shrink-0 ml-2">
                      <Lock className="w-3 h-3" />
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{workshop.description}</p>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Lightbulb className="w-3.5 h-3.5" />
                    {workshop.foresight_session_count} foresight
                  </span>
                  <span className="flex items-center gap-1">
                    <FileText className="w-3.5 h-3.5" />
                    {workshop.writing_session_count} writing
                  </span>
                </div>
              </div>
            ))}

            {workshops.length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No workshops yet. {canCreate ? 'Create your first workshop to get started.' : "You haven't been added to a workshop yet -- ask your facilitator to add you."}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); resetCreateForm(); }}
        title="Create New Workshop"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Workshop Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              placeholder="Q1 2025 Strategic Foresight Workshop"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              rows="3"
              placeholder="A brief description of this workshop..."
            />
          </div>
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={isPasswordProtected} onChange={e => setIsPasswordProtected(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-[#01006A] focus:ring-[#01006A]" />
              <span className="text-sm font-medium text-gray-700">Password-protect this workshop</span>
            </label>
            {isPasswordProtected && (
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
                placeholder="Set a password for participants" />
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={createWorkshop}
              disabled={!newName || isCreating || (isPasswordProtected && !newPassword)}
              className="flex-1 bg-[#01006A] text-white py-2 rounded-lg hover:bg-[#010059] disabled:bg-gray-300"
            >
              {isCreating ? 'Creating...' : 'Create'}
            </button>
            <button
              onClick={() => { setShowCreateModal(false); resetCreateForm(); }}
              className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {showArchiveModal && (
        <ArchivedWorkshopsModal
          onClose={() => { setShowArchiveModal(false); loadWorkshops(); }}
        />
      )}
    </div>
  );
};

export default WorkshopsView;
